import { createApi } from '@reduxjs/toolkit/query/react';
import API_ROUTES from 'services/apiRoutes';
import { baseQueryWithAuth } from 'services/baseQueryWithAuth';

export interface AdminElection {
  election_id: string;
  display_info: string;
  election_date: string;
  state: string;
  election_level: string;
  type: string;
}

export interface CreateCandidatePayload {
  display_name: string;
  legal_name: string;
  subtype: string;
  photo_id: string;
  de_url_id: string;
  is_public_searchable: boolean;
  election_id: string;
  is_incumbent?: boolean;
  is_self_funded?: boolean;
  is_pay_to_play_issue?: boolean;
  status?: RaceStatus;
  primary_contact_name?: string;
  primary_contact_email?: string;
  recipient_status?: string;
  is_stripe_authorized?: boolean;
  onboarding_form_complete?: boolean;
  de_status?: string;
  public_fundraising_query_str?: string;
}

export interface CreateCandidateResponse {
  entity_id: string;
  recipient_id: string;
  race_id: string;
}

export interface SelectedElectionItem {
  election_id: string;
  display_info: string;
  state: string;
  type: string;
  election_date: string;
  is_incumbent?: boolean;
  is_self_funded?: boolean;
  is_pay_to_play_issue?: boolean;
  status?: RaceStatus;
}

export type RaceStatus = 'UPCOMING' | 'ACTIVE' | 'COMPLETE';

export interface CandidateRace {
  race_id: string;
  election_id: string;
  election_display_info: string;
  election_date: string;
  state: string;
  status: RaceStatus;
  start_date: string;
  end_date: string;
  political_party: 'DEMOCRAT' | 'INDEPENDENT';
  is_incumbent: boolean;
  is_self_funded: boolean;
  is_pay_to_play_issue: boolean;
}

export interface PatchRacePayload {
  status?: RaceStatus;
  is_incumbent?: boolean;
  is_self_funded?: boolean;
  is_pay_to_play_issue?: boolean;
}

export interface CreateRacePayload {
  election_id: string;
  status: RaceStatus;
  is_incumbent?: boolean;
  is_self_funded?: boolean;
  is_pay_to_play_issue?: boolean;
}

export interface CreateRaceResponse {
  race_id: string;
  election_id: string;
  message: string;
}

export const adminCandidatesApi = createApi({
  reducerPath: 'adminCandidatesApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['CandidateRaces'],
  endpoints: builder => ({
    getElections: builder.query<AdminElection[], { search?: string }>({
      query: ({ search }) => {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        // Backend caps requests at MAX_ELECTION_SEARCH_LIMIT (100, see shared/constants/race.py)
        params.append('limit', '100');
        const qs = params.toString();
        return `${API_ROUTES.GET_ELECTIONS}${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (response: { results: AdminElection[] }) => response.results,
    }),

    createCandidate: builder.mutation<
      CreateCandidateResponse,
      { body: CreateCandidatePayload; activeRole: string; confirmDuplicate?: boolean }
    >({
      query: ({ body, activeRole, confirmDuplicate }) => ({
        url: `${API_ROUTES.CREATE_CANDIDATE(activeRole)}${confirmDuplicate ? '&confirm_duplicate=true' : ''}`,
        method: 'POST',
        body,
      }),
    }),

    getRaces: builder.query<CandidateRace[], { recipientId: string }>({
      query: ({ recipientId }) => API_ROUTES.GET_RACES(recipientId),
      transformResponse: (response: { results: CandidateRace[] } | CandidateRace[]) =>
        Array.isArray(response) ? response : response.results,
      providesTags: (result, error, { recipientId }) => [
        { type: 'CandidateRaces' as const, id: recipientId },
      ],
    }),

    createRace: builder.mutation<
      CreateRaceResponse,
      { recipientId: string; body: CreateRacePayload }
    >({
      query: ({ recipientId, body }) => ({
        url: API_ROUTES.CREATE_RACE(recipientId),
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { recipientId }) => [
        { type: 'CandidateRaces' as const, id: recipientId },
      ],
    }),

    patchRace: builder.mutation<
      void,
      { recipientId: string; raceId: string; body: PatchRacePayload }
    >({
      query: ({ recipientId, raceId, body }) => ({
        url: API_ROUTES.PATCH_RACE(recipientId, raceId),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { recipientId }) => [
        { type: 'CandidateRaces' as const, id: recipientId },
      ],
    }),
  }),
});

export const {
  useLazyGetElectionsQuery,
  useCreateCandidateMutation,
  useGetRacesQuery,
  useCreateRaceMutation,
  usePatchRaceMutation,
} = adminCandidatesApi;
